import { defineStore } from "pinia";
import pinia from "@/plugins/piniaStore";
import { useDropdownUser } from "./storeDropdownData";
import { IPageLayout } from "@/helpers/api";
import { getIcon } from "@/helpers/api";
import { useDeviceStatus } from "@/stores/deviceStatus";
import { toRefs, ref, computed, reactive, watch } from "vue";
import dataTreeManager from "@/persistentStorage/wks_storage/dataTreeManager";
import { useAccountDataUser } from "@/modules/5.account/stores/accountDataStore";

const DOWNLOAD_BUTTON_INDEX = 3;
const BADGES_BUTTON_INDEX = 2;

interface IHomepageUserResponse {
  activeFooterButtonsMe: boolean[];
  activeFooterButtonsMe_web: boolean[];
  activeFooterButtonsMentee: boolean[];
  activeFooterButtonsMentee_web: boolean[];
  activeFooterButtonsClass: boolean[];
  activeFooterButtonsClass_web: boolean[];
}

interface IFooterButton {
  label: string;
  icon: string;
  route: string;
  enabled: boolean;
  selected: boolean;
}

interface IFooterData {
  isOpen: boolean;
  firstLoad: boolean;
  footerTextLabelsUser: string[];
  footerTextLabelsClass: string[];

  activeFooterButtonsMe: boolean[];
  activeFooterButtonsMentee: boolean[];
  activeFooterButtonsClass: boolean[];
  activeFooterButtonsMe_web: boolean[];
  activeFooterButtonsMentee_web: boolean[];
  activeFooterButtonsClass_web: boolean[];
  nFooterButtons: number;
  footerSelectedButtonUser: number;
  footerUserIcons: string[];
  footerUserIconsSelected: string[];
  footerUserIconsSelectedMobile: string[];
  footerUserIconsSelectedWeb: string[];
  footerSelectedButtonClass: number;
  footerClassIcons: string[];
  footerClassIconsSelected: string[];
  footerClassIconsSelectedMobile: string[];
  footerClassIconsSelectedWeb: string[];
  footerRoutesUser: string[];
  footerRoutesUserArchive: string[];
  footerRoutesClass: string[];
  isDownloadButtonEnabled: boolean;
}

interface IFooterLayout extends IPageLayout {
  labels: {
    user: string[];
    class: string[];
  };
  commands: {
    routers_class: string[];
    routers_user: string[];
    footer_button_select: number;
    user_icons: string[];
    user_icons_selected: string[];
    class_icons: string[];
    user_icons_selected_web: string[];
    class_icons_selected: string[];
    class_icons_selected_web: string[];
    routers_user_archive: string[];
  };
}

const useFooterData = defineStore("FooterData", () => {
  const state = reactive<IFooterData>({
    isOpen: false,
    firstLoad: true,

    activeFooterButtonsMe: [false, false, false, false, false],
    activeFooterButtonsMentee: [false, false, false, false, false],
    activeFooterButtonsClass: [false, false, false, false, false],

    activeFooterButtonsMe_web: [false, false, false, false],
    activeFooterButtonsMentee_web: [false, false, false, false],
    activeFooterButtonsClass_web: [false, false, false, false, false],

    nFooterButtons: 0,
    footerSelectedButtonUser: 0,
    footerSelectedButtonClass: 0,

    footerUserIcons: [],
    footerUserIconsSelected: [],
    footerUserIconsSelectedMobile: [],
    footerUserIconsSelectedWeb: [],

    footerClassIcons: [],
    footerClassIconsSelected: [],
    footerClassIconsSelectedMobile: [],
    footerClassIconsSelectedWeb: [],

    footerTextLabelsUser: [],
    footerTextLabelsClass: [],

    footerRoutesUser: [],
    footerRoutesUserArchive: [],
    footerRoutesClass: [],

    isDownloadButtonEnabled: false,
  });

  const { isWeb } = toRefs(useDeviceStatus());

  const getFooterButtons = computed(() => {
    const dropdownUser = useDropdownUser();
    const accountStore = useAccountDataUser();

    const userSelectedIcons = isWeb.value
      ? state.footerUserIconsSelectedWeb
      : state.footerUserIconsSelectedMobile;

    const classSelectedIcons = isWeb.value
      ? state.footerClassIconsSelectedWeb
      : state.footerClassIconsSelectedMobile;

    if (dropdownUser.getKind === "classroom") {
      return state.footerTextLabelsClass.map((label, i) => ({
        label,
        icon:
          state.footerSelectedButtonClass === i
            ? classSelectedIcons[i]
            : state.footerClassIcons[i],
        route: state.footerRoutesClass[i],
        enabled: state.activeFooterButtonsClass[i],
        selected: i === state.footerSelectedButtonClass,
      }));
    }

    const routes = accountStore.inArchive
      ? state.footerRoutesUserArchive
      : state.footerRoutesUser;

    return state.footerTextLabelsUser.map((label, i) => {
      const icon =
        state.footerSelectedButtonUser === i
          ? userSelectedIcons[i]
          : state.footerUserIcons[i];

      let enabled = true;
      if (dropdownUser.getKind === "me") {
        enabled = state.activeFooterButtonsMe[i];
        // if (
        //   (i === DOWNLOAD_BUTTON_INDEX && !state.isDownloadButtonEnabled) ||
        //   (i === BADGES_BUTTON_INDEX &&
        //     dropdownUser.$state.dropdownList[0].stats[0] === 0)
        // ) {
        //   enabled = state.activeFooterButtonsMe[i];
        // } else {
        //   enabled = state.activeFooterButtonsMe[i];
        // }
      } else {
        enabled = state.activeFooterButtonsMentee[i];
      }

      return {
        label,
        icon,
        route: routes[i],
        enabled,
        selected: i === state.footerSelectedButtonUser,
      };
    });
  });

  const getSelectedFooterButton = computed(() => {
    const dropdownUser = useDropdownUser();
    return dropdownUser.getKind === "classroom"
      ? state.footerSelectedButtonClass
      : state.footerSelectedButtonUser;
  });

  function isFirstLoad() {
    if (state.firstLoad) {
      state.firstLoad = false;
      return true;
    }
    return false;
  }

  function selectButtonUser(i: number) {
    state.footerSelectedButtonUser = i;
  }

  function selectButtonClass(i: number) {
    state.footerSelectedButtonClass = i;
  }

  async function setFooterData(layout: IFooterLayout) {
    console.log("+++++++++ setFooterData +++++++++");
    console.log("layout: ", layout);

    const localFooterUserIcons: string[] = [];
    for (const icn of layout.commands.user_icons) {
      localFooterUserIcons.push((await getIcon("1100F", icn)) as string);
    }

    const localFooterClassIcons: string[] = [];
    for (const icn of layout.commands.class_icons) {
      localFooterClassIcons.push((await getIcon("1100F", icn)) as string);
    }

    const userSelMobile: string[] = [];
    for (const icn of layout.commands.user_icons_selected) {
      userSelMobile.push((await getIcon("1100F", icn)) as string);
    }

    const userSelWeb: string[] = [];
    for (const icn of layout.commands.user_icons_selected_web) {
      userSelWeb.push((await getIcon("1100F", icn)) as string);
    }

    const classSelMobile: string[] = [];
    for (const icn of layout.commands.class_icons_selected) {
      classSelMobile.push((await getIcon("1100F", icn)) as string);
    }

    const classSelWeb: string[] = [];
    for (const icn of layout.commands.class_icons_selected_web) {
      classSelWeb.push((await getIcon("1100F", icn)) as string);
    }

    $patch({
      footerUserIcons: localFooterUserIcons,
      footerClassIcons: localFooterClassIcons,

      footerUserIconsSelected: userSelMobile,
      footerClassIconsSelected: classSelMobile,
      footerUserIconsSelectedMobile: userSelMobile,
      footerUserIconsSelectedWeb: userSelWeb,
      footerClassIconsSelectedMobile: classSelMobile,
      footerClassIconsSelectedWeb: classSelWeb,

      nFooterButtons: layout.labels.user.length,
      footerTextLabelsUser: layout.labels.user,
      footerTextLabelsClass: layout.labels.class,
      footerRoutesUser: layout.commands.routers_user,
      footerRoutesUserArchive: layout.commands.routers_user_archive,
      footerRoutesClass: layout.commands.routers_class,
    });
  }

  function setHomepageUserFooterData(layout: IHomepageUserResponse) {
    state.activeFooterButtonsMe = layout.activeFooterButtonsMe;
    state.activeFooterButtonsMentee = layout.activeFooterButtonsMentee;
    state.activeFooterButtonsClass = layout.activeFooterButtonsClass;
    state.activeFooterButtonsMe_web = layout.activeFooterButtonsMe_web;
    state.activeFooterButtonsMentee_web = layout.activeFooterButtonsMentee_web;
    state.activeFooterButtonsClass_web = layout.activeFooterButtonsClass_web;
  }

  function $patch(data: Partial<IFooterData>) {
    Object.assign(state, data);
  }

  const { uuid } = toRefs(useAccountDataUser());
  const { isNetConnected } = toRefs(useDeviceStatus());

  watch(
    [dataTreeManager.stateHash, uuid],
    async () => {
      const dataTree = await dataTreeManager.loadAndGet(uuid.value);
      const keys = dataTree.worksheets.keys();
      if ((keys && Array.from(keys).length > 0) || !isNetConnected.value) {
        state.isDownloadButtonEnabled = true;
      } else {
        state.isDownloadButtonEnabled = false;
      }
    },
    { immediate: true },
  );

  function setOfflineMode() {
    state.activeFooterButtonsMe = [false, true, false, false, false];
  }

  return {
    ...toRefs(state),
    getFooterButtons,
    getSelectedFooterButton,
    isFirstLoad,
    selectButtonUser,
    selectButtonClass,
    setFooterData,
    setHomepageUserFooterData,
    $patch,
    setOfflineMode,
  };
});

//******************************************************************

export {
  useFooterData,
  IFooterData,
  IFooterButton,
  IFooterLayout,
  IHomepageUserResponse,
};
